(function () {
  class TryOnExperience {
    constructor(root) {
      this.root = root;
      this.productSection = this.root.closest('[id^="MainProduct-"]') || document;
      this.state = {
        data: this.readData(),
        faceImage: null,
        glassesImage: null,
        faceRect: null,
        leftEye: null,
        rightEye: null,
        pdMm: null,
        pdValid: true,
        selectedColor: null,
        activeImageUrl: null,
      };
      this.stream = null;
      this.draggingMarker = null;
      this.suppressWorkspaceClick = false;
      this.variantChangeUnsubscriber = null;
      this.cacheDom();
      this.bindEvents();
      this.syncGlassesImageFromSelection();
      this.updateAvailability();
      this.logDebugSnapshot('init');
      this.updateStatus(
        'Start with a portrait photo. You can also use your camera when you are ready.',
        'Choose a model, upload, or use camera.'
      );
    }

    readData(source = this.root) {
      const script = source.querySelector('script[type="application/json"]');
      if (!script || !script.textContent) return {};

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.error('Failed to parse try-on data', error);
        return {};
      }
    }

    cacheDom() {
      this.openButton = this.root.querySelector('[data-try-on-open]');
      this.closeButton = this.root.querySelector('[data-try-on-close]');
      this.dialog = this.root.querySelector('[data-try-on-dialog]');
      this.uploadButton = this.root.querySelector('[data-try-on-upload]');
      this.uploadInput = this.root.querySelector('[data-try-on-upload-input]');
      this.cameraButton = this.root.querySelector('[data-try-on-camera-open]');
      this.cameraCancelButton = this.root.querySelector('[data-try-on-camera-cancel]');
      this.captureButton = this.root.querySelector('[data-try-on-capture]');
      this.resetMarkersButton = this.root.querySelector('[data-try-on-reset-markers]');
      this.clearButton = this.root.querySelector('[data-try-on-clear]');
      this.cameraPanel = this.root.querySelector('[data-try-on-camera]');
      this.video = this.root.querySelector('[data-try-on-video]');
      this.workspace = this.root.querySelector('[data-try-on-workspace]');
      this.canvas = this.root.querySelector('[data-try-on-canvas]');
      this.loupe = this.root.querySelector('[data-try-on-loupe]');
      this.loupeCanvas = this.root.querySelector('[data-try-on-loupe-canvas]');
      this.leftMarker = this.root.querySelector('[data-marker="left"]');
      this.rightMarker = this.root.querySelector('[data-marker="right"]');
      this.emptyState = this.root.querySelector('[data-try-on-empty]');
      this.mobilePrompt = this.root.querySelector('[data-try-on-mobile-prompt]');
      this.mobilePromptText = this.root.querySelector('[data-try-on-mobile-prompt-text]');
      this.status = this.root.querySelector('[data-try-on-status]');
      this.statusText = this.root.querySelector('.try-on-experience__status-text');
      this.referencesCopy = this.root.querySelector('.try-on-experience__references-copy');
      this.error = this.root.querySelector('[data-try-on-error]');
      this.pdInputs = Array.from(this.root.querySelectorAll('[data-try-on-pd]'));
      this.pdErrors = Array.from(this.root.querySelectorAll('[data-try-on-pd-error]'));
      this.referenceButtons = Array.from(this.root.querySelectorAll('[data-try-on-reference]'));

      this.resizeObserver = new ResizeObserver(() => this.drawScene());
      if (this.workspace) this.resizeObserver.observe(this.workspace);
    }

    bindEvents() {
      this.openButton?.addEventListener('click', () => this.openDialog());
      this.closeButton?.addEventListener('click', () => this.closeDialog());
      this.dialog?.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.closeDialog();
      });
      this.dialog?.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.closeDialog();
      });
      this.uploadButton?.addEventListener('click', () => this.uploadInput?.click());
      this.uploadInput?.addEventListener('change', (event) => this.handleUpload(event));
      this.cameraButton?.addEventListener('click', () => this.openCamera());
      this.cameraCancelButton?.addEventListener('click', () => this.stopCameraAndHide());
      this.captureButton?.addEventListener('click', () => this.capturePhoto());
      this.resetMarkersButton?.addEventListener('click', () => this.resetMarkers());
      this.clearButton?.addEventListener('click', () => this.clearPreview());
      this.workspace?.addEventListener('pointerdown', (event) => this.handleWorkspacePointerDown(event));
      this.workspace?.addEventListener('click', (event) => this.handleWorkspaceClick(event));
      this.pdInputs.forEach((input) => {
        input.addEventListener('input', (event) => {
          this.sanitizePdInput(event);
          this.handlePdInput({ commit: false, input });
        });
        input.addEventListener('blur', () => this.handlePdInput({ commit: true, input }));
        input.addEventListener('change', () => this.handlePdInput({ commit: true, input }));
      });
      this.leftMarker?.addEventListener('pointerdown', (event) => this.startMarkerDrag(event, 'left'));
      this.rightMarker?.addEventListener('pointerdown', (event) => this.startMarkerDrag(event, 'right'));
      this.referenceButtons.forEach((button) =>
        button.addEventListener('click', () => this.handleReferenceSelection(button))
      );
      this.productSection?.addEventListener('change', this.handleProductChange);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const sectionId = event?.data?.sectionId;
          if (sectionId && !this.productSection?.contains(document.getElementById(`product-form-${sectionId}`))) return;
          this.handleProductSelectionSync();
        });
      }

      window.addEventListener('pointermove', this.handlePointerMove);
      window.addEventListener('pointerup', this.handlePointerUp);
    }

    handleProductChange = () => {
      window.requestAnimationFrame(() => this.handleProductSelectionSync());
    };

    async handleProductSelectionSync() {
      const didChange = this.syncGlassesImageFromSelection();
      this.updateAvailability();
      this.logDebugSnapshot('selection-sync');
      if (didChange) {
        this.clearError();
        if (this.dialog?.open) {
          try {
            await this.ensureGlassesImage();
          } catch (error) {
            console.error(error);
            this.showError('The try-on frame image for the selected color could not be loaded right now.');
          }
        }
        this.drawScene();
      }
    }

    updateAvailability() {
      const isAvailable = Boolean(this.state.data.isAvailable && this.state.activeImageUrl && this.state.data.frameWidthMm);
      if (this.openButton) this.openButton.disabled = !isAvailable;

      console.info('[TryOn] availability-check', {
        productId: this.state.data.productId,
        liquidIsAvailable: this.state.data.isAvailable,
        hasFrameWidth: Boolean(this.state.data.frameWidthMm),
        hasDefaultImage: Boolean(this.state.data.defaultImageUrl),
        activeImageUrl: this.state.activeImageUrl,
        resolvedAvailable: isAvailable,
      });
    }

    getSelectedColor() {
      const swatchFieldsets = this.productSection.querySelectorAll('fieldset.color-swatches');

      for (const fieldset of swatchFieldsets) {
        const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
        if (checkedInput?.value) return checkedInput.value;

        const selectedOption = fieldset.querySelector('.selected-option');
        if (selectedOption?.textContent?.trim()) return selectedOption.textContent.trim();
      }

      const selectInputs = this.productSection.querySelectorAll('select[name^="options["]');
      for (const select of selectInputs) {
        const label = select.closest('.product-form__input')?.querySelector('.form__label')?.textContent || select.name;
        if (this.isColorLabel(label) && select.value) return select.value;
      }

      const radioInputs = this.productSection.querySelectorAll('fieldset input[type="radio"]:checked');
      for (const input of radioInputs) {
        const fieldset = input.closest('fieldset');
        const label = fieldset?.querySelector('legend')?.textContent || fieldset?.className || '';
        if (this.isColorLabel(label) && input.value) return input.value;
      }

      return null;
    }

    isColorLabel(label) {
      return /color|colour|couleur|farbe|cor|kleur|tono|colore/i.test(label || '');
    }

    normalizeColorKey(value) {
      return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/^tryon_/i, '')
        .replace(/[_\s-]+/g, '');
    }

    resolveActiveImageUrl(selectedColor) {
      const imagesByColor = this.state.data.imagesByColor || {};
      if (selectedColor) {
        const normalizedColor = this.normalizeColorKey(selectedColor);
        const exactMatch = Object.entries(imagesByColor).find(([key]) => this.normalizeColorKey(key) === normalizedColor);
        if (exactMatch) return exactMatch[1];
      }

      return this.state.data.defaultImageUrl || null;
    }

    syncGlassesImageFromSelection() {
      const nextColor = this.getSelectedColor();
      const nextImageUrl = this.resolveActiveImageUrl(nextColor);
      const didChange =
        this.state.selectedColor !== nextColor ||
        this.state.activeImageUrl !== nextImageUrl;

      this.state.selectedColor = nextColor;
      this.state.activeImageUrl = nextImageUrl;

      if (didChange) {
        this.state.glassesImage = null;
      }

      console.info('[TryOn] image-resolution', {
        productId: this.state.data.productId,
        selectedColor: nextColor,
        availableColorKeys: Object.keys(this.state.data.imagesByColor || {}),
        resolvedImageUrl: nextImageUrl,
        didChange,
      });

      return didChange;
    }

    async openDialog() {
      if (!this.dialog || this.openButton?.disabled) return;
      document.body.style.overflow = 'hidden';
      this.dialog.showModal();
      this.handleProductSelectionSync();

      try {
        await this.ensureGlassesImage();
      } catch (error) {
        console.error(error);
        this.showError('The try-on frame image for the selected color could not be loaded right now.');
      }

      this.drawScene();
    }

    closeDialog() {
      if (!this.dialog?.open) return;
      document.body.style.overflow = '';
      this.dialog.close();
      this.stopCamera();
      this.toggleCameraPanel(false);
    }

    async handleUpload(event) {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;
      const file = input.files && input.files[0];
      if (!file) return;

      if (!file.type.startsWith('image/')) {
        this.showError('Please upload a JPG, PNG, or WEBP image.');
        return;
      }

      const dataUrl = await this.readFileAsDataUrl(file);
      await this.setFaceImage(dataUrl);
      input.value = '';
    }

    async openCamera() {
      if (!navigator.mediaDevices?.getUserMedia) {
        this.showError('Camera access is not supported in this browser.');
        return;
      }

      try {
        this.stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user' },
          audio: false,
        });

        if (this.video) this.video.srcObject = this.stream;
        this.toggleCameraPanel(true);
        this.clearError();
        this.updateStatus(
          'Position your face in the frame, then capture a photo to start calibration.',
          'Camera ready. Capture your photo.'
        );
      } catch (error) {
        console.error(error);
        this.showError('Camera access was blocked. You can upload a photo instead.');
      }
    }

    stopCameraAndHide() {
      this.stopCamera();
      this.toggleCameraPanel(false);
      this.updateStatus(
        'Camera closed. Upload a portrait photo or reopen the camera to continue.',
        'Choose a model, upload, or use camera.'
      );
    }

    async capturePhoto() {
      if (!(this.video instanceof HTMLVideoElement)) return;
      const width = this.video.videoWidth;
      const height = this.video.videoHeight;
      if (!width || !height) {
        this.showError('Camera is not ready yet. Please try again.');
        return;
      }

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) return;

      const isPortraitViewport = window.innerHeight > window.innerWidth;
      const needsRotate = isPortraitViewport && width > height;
      const shouldMirror = true;

      if (needsRotate) {
        canvas.width = height;
        canvas.height = width;
        context.translate(canvas.width / 2, canvas.height / 2);
        context.rotate(Math.PI / 2);
        if (shouldMirror) {
          context.scale(-1, 1);
        }
        context.drawImage(this.video, -width / 2, -height / 2, width, height);
      } else {
        canvas.width = width;
        canvas.height = height;
        if (shouldMirror) {
          context.translate(canvas.width, 0);
          context.scale(-1, 1);
        }
        context.drawImage(this.video, 0, 0, width, height);
      }

      const imageUrl = canvas.toDataURL('image/png');
      this.stopCamera();
      this.toggleCameraPanel(false);
      await this.setFaceImage(imageUrl);
    }

    async setFaceImage(src) {
      const image = await this.loadImage(src);
      this.state.faceImage = image;
      this.state.leftEye = null;
      this.state.rightEye = null;
      this.hideMarkers();
      this.hideLoupe();
      this.clearError();
      this.updateStatus(
        'Tap the center of your left pupil, then tap the center of your right pupil.',
        'Tap left pupil, then right pupil.'
      );
      this.drawScene();
    }

    async handleReferenceSelection(button) {
      if (!(button instanceof HTMLElement)) return;

      const imageUrl = button.dataset.referenceSrc;
      const leftRatio = Number(button.dataset.leftRatio);
      const leftTopRatio = Number(button.dataset.leftTopRatio);
      const rightRatio = Number(button.dataset.rightRatio);
      const rightTopRatio = Number(button.dataset.rightTopRatio);
      const pdMm = Number(button.dataset.pdMm);

      if (!imageUrl) {
        this.showError('The selected reference portrait could not be loaded.');
        return;
      }

      try {
        const image = await this.loadImage(imageUrl);
        this.state.faceImage = image;
        this.state.leftEye = {
          x: leftRatio,
          y: leftTopRatio,
        };
        this.state.rightEye = {
          x: rightRatio,
          y: rightTopRatio,
        };
        this.state.pdMm = pdMm;
        this.state.pdValid = true;

        this.syncPdInputs(String(pdMm));

        this.clearError();
        this.clearPdFieldError();
        this.updateStatus(
          'Reference portrait loaded with the original marker positions and PD.',
          'Reference loaded. Adjust markers or PD.'
        );
        this.drawScene();
      } catch (error) {
        console.error(error);
        this.showError('The selected reference portrait could not be loaded.');
      }
    }

    handleWorkspaceClick(event) {
      if (this.suppressWorkspaceClick) {
        this.suppressWorkspaceClick = false;
        return;
      }
      if (!this.state.faceImage || this.draggingMarker) return;
      const point = this.getNormalizedPoint(event);
      if (!point) return;
      if (this.getMarkerAtPointerEvent(event)) {
        return;
      }

      if (!this.state.leftEye) {
        this.state.leftEye = point;
        this.hideLoupe();
        this.updateStatus(
          'Left pupil saved. Now tap the center of your right pupil.',
          'Left pupil saved. Tap right pupil.'
        );
        this.drawScene();
        return;
      }

      if (!this.state.rightEye) {
        this.state.rightEye = point;
        this.hideLoupe();
        this.updateStatus(
          'Both markers are set. Drag either marker to refine the fit, then enter your PD.',
          'Markers set. Enter your PD.'
        );
        this.drawScene();
      }
    }

    handleWorkspacePointerDown(event) {
      if (!this.state.faceImage || this.draggingMarker) return;
      const marker = this.getMarkerAtPointerEvent(event);
      if (marker) {
        this.suppressWorkspaceClick = true;
        this.startMarkerDrag(event, marker);
        return;
      }

      const point = this.getNormalizedPoint(event);
      if (!point) return;

      const nextMarker = !this.state.leftEye ? 'left' : !this.state.rightEye ? 'right' : null;
      if (nextMarker) {
        this.suppressWorkspaceClick = true;
        if (nextMarker === 'left') {
          this.state.leftEye = point;
          this.updateStatus(
            'Left pupil saved. Now tap the center of your right pupil.',
            'Left pupil saved. Tap right pupil.'
          );
        } else {
          this.state.rightEye = point;
          this.updateStatus(
            'Both markers are set. Drag either marker to refine the fit, then enter your PD.',
            'Markers set. Enter your PD.'
          );
        }
        this.startMarkerDrag(event, nextMarker);
        this.drawScene();
        return;
      }

      if (this.shouldUseLoupe()) {
        this.updateLoupe(event);
      }
    }

    handlePdInput({ commit = false, input = null } = {}) {
      const activeInput = input instanceof HTMLInputElement ? input : this.pdInputs[0];
      const value = Number(activeInput?.value);
      const hasValue = Number.isFinite(value) && value > 0;
      const { pdMin, pdMax } = this.getPdBounds();
      let nextPd = hasValue ? value : null;
      const isOutOfRange = hasValue && (value < pdMin || value > pdMax);

      if (hasValue && commit) {
        if (value < pdMin) nextPd = pdMin;
        if (value > pdMax) nextPd = pdMax;
      }

      if (activeInput && commit && nextPd !== null && nextPd !== value) {
        activeInput.value = String(nextPd);
      }

      this.syncPdInputs(activeInput?.value || '', activeInput);

      this.state.pdMm = nextPd;
      this.state.pdValid =
        !hasValue ||
        (!isOutOfRange || commit);

      this.drawScene();

      if (isOutOfRange && commit) {
        const rangeLabel = this.getPdRangeLabel();
        this.setPdFieldError(`PD must be between ${rangeLabel}.`);
        this.updateStatus(
          `PD was adjusted to stay within ${rangeLabel}.`,
          `PD adjusted to ${rangeLabel}.`
        );
        return;
      }

      if (isOutOfRange) {
        const rangeLabel = this.getPdRangeLabel();
        this.setPdFieldError(`PD must be between ${rangeLabel}.`);
        this.updateStatus(`PD must stay within ${rangeLabel}.`, `PD must be ${rangeLabel}.`);
        return;
      }

      this.clearPdFieldError();

      if (this.state.pdMm) {
        this.updateStatus(
          'Preview updated. Drag the markers or adjust your PD for a better fit preview.',
          'Preview ready. Fine-tune markers or PD.'
        );
      } else {
        const rangeLabel = this.getPdRangeLabel();
        this.updateStatus(
          rangeLabel
            ? `Enter your PD in millimeters to scale the frame preview. Valid range: ${rangeLabel}.`
            : 'Enter your PD in millimeters to scale the frame preview.',
          rangeLabel ? `Enter PD. Valid range: ${rangeLabel}.` : 'Enter your PD.'
        );
      }
    }

    sanitizePdInput(event) {
      const input = event?.target;
      if (!(input instanceof HTMLInputElement)) return;

      const sanitized = input.value
        .replace(/[^0-9.]/g, '')
        .replace(/(\..*)\./g, '$1');

      if (sanitized !== input.value) {
        input.value = sanitized;
      }
    }

    startMarkerDrag(event, marker) {
      event.preventDefault();
      event.stopPropagation();
      this.draggingMarker = marker;
      this.root.classList.add('try-on-experience--dragging-markers');
      this.updateLoupe(event);
    }

    handlePointerMove = (event) => {
      if (!this.draggingMarker) return;
      const point = this.getNormalizedPoint(event);
      if (!point) return;

      if (this.draggingMarker === 'left') {
        this.state.leftEye = point;
      } else {
        this.state.rightEye = point;
      }

      this.updateLoupe(event);
      this.drawScene();
    };

    handlePointerUp = () => {
      this.draggingMarker = null;
      this.root.classList.remove('try-on-experience--dragging-markers');
      this.hideLoupe();
    };

    resetMarkers() {
      if (!this.state.faceImage) {
        this.updateStatus(
          'Upload a portrait photo or use your camera to start calibration.',
          'Choose a model, upload, or use camera.'
        );
        return;
      }

      this.state.leftEye = null;
      this.state.rightEye = null;
      this.clearError();
      this.hideMarkers();
      this.hideLoupe();
      this.updateStatus(
        'Markers cleared. Tap the center of your left pupil, then the center of your right pupil.',
        'Markers reset. Tap left pupil, then right pupil.'
      );
      this.drawScene();
    }

    clearPreview() {
      this.state.faceImage = null;
      this.state.leftEye = null;
      this.state.rightEye = null;
      this.state.pdMm = null;
      this.state.pdValid = true;
      this.syncPdInputs('');
      this.hideMarkers();
      this.hideLoupe();
      this.clearError();
      this.clearPdFieldError();
      this.stopCameraAndHide();
      this.updateStatus(
        'Preview cleared. Upload a portrait photo or open your camera to start again.',
        'Preview cleared. Choose a model, upload, or use camera.'
      );
      this.drawScene();
    }

    async ensureGlassesImage() {
      const imageUrl = this.state.activeImageUrl;
      if (!imageUrl) return null;
      if (this.state.glassesImage?.src === imageUrl) return this.state.glassesImage;
      this.state.glassesImage = await this.loadImage(imageUrl);
      return this.state.glassesImage;
    }

    drawScene() {
      if (!(this.canvas instanceof HTMLCanvasElement) || !(this.workspace instanceof HTMLElement)) return;

      const width = this.workspace.clientWidth;
      const height = this.workspace.clientHeight;
      if (!width || !height) return;
      this.workspace.style.setProperty('--try-on-marker-size', `${width / 16}px`);

      const dpr = window.devicePixelRatio || 1;
      this.canvas.width = Math.round(width * dpr);
      this.canvas.height = Math.round(height * dpr);
      this.canvas.style.width = `${width}px`;
      this.canvas.style.height = `${height}px`;

      const context = this.canvas.getContext('2d');
      if (!context) return;

      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      context.clearRect(0, 0, width, height);

      if (!this.state.faceImage) {
        this.state.faceRect = null;
        this.emptyState?.removeAttribute('hidden');
        this.updateMobileStepUi();
        return;
      }

      this.emptyState?.setAttribute('hidden', '');
      const faceRect = this.getContainRect(this.state.faceImage.width, this.state.faceImage.height, width, height);
      this.state.faceRect = faceRect;
      context.drawImage(this.state.faceImage, faceRect.x, faceRect.y, faceRect.width, faceRect.height);

      const leftPoint = this.getRenderedPoint(this.state.leftEye);
      const rightPoint = this.getRenderedPoint(this.state.rightEye);
      this.positionMarker(this.leftMarker, leftPoint);
      this.positionMarker(this.rightMarker, rightPoint);

      const isPreviewReady = Boolean(
        leftPoint && rightPoint && this.state.pdMm && this.state.glassesImage && this.state.data.frameWidthMm
      );
      this.root.classList.toggle('try-on-experience--preview-ready', isPreviewReady);

      if (
        !leftPoint ||
        !rightPoint ||
        !this.state.pdMm ||
        !this.state.pdValid ||
        !this.state.glassesImage ||
        !this.state.data.frameWidthMm
      ) {
        this.updateMobileStepUi();
        return;
      }

      const pupilDistancePx = Math.hypot(rightPoint.x - leftPoint.x, rightPoint.y - leftPoint.y);
      if (!pupilDistancePx) return;

      const pxPerMm = pupilDistancePx / this.state.pdMm;
      const frameWidthPx = this.state.data.frameWidthMm * pxPerMm;
      const angleRad = Math.atan2(rightPoint.y - leftPoint.y, rightPoint.x - leftPoint.x);
      const centerX = (leftPoint.x + rightPoint.x) / 2;
      const centerY = (leftPoint.y + rightPoint.y) / 2;
      const aspectRatio = this.state.glassesImage.height / this.state.glassesImage.width;
      const frameHeightPx = frameWidthPx * aspectRatio;

      context.save();
      context.translate(centerX, centerY);
      context.rotate(angleRad);
      context.drawImage(
        this.state.glassesImage,
        -frameWidthPx / 2,
        -frameHeightPx / 2,
        frameWidthPx,
        frameHeightPx
      );
      context.restore();
      this.updateMobileStepUi();
    }

    updateMobileStepUi() {
      const hasFace = Boolean(this.state.faceImage);
      const hasLeftEye = Boolean(this.state.leftEye);
      const hasRightEye = Boolean(this.state.rightEye);
      const showPdField = hasFace && hasLeftEye && hasRightEye;
      const showPrompt = hasFace && !showPdField;

      this.root.classList.toggle('try-on-experience--mobile-pd-visible', showPdField);
      this.root.classList.toggle('try-on-experience--mobile-prompt-visible', showPrompt);

      if (!(this.mobilePrompt instanceof HTMLElement) || !(this.mobilePromptText instanceof HTMLElement)) return;

      if (!showPrompt) {
        this.mobilePrompt.hidden = true;
        this.mobilePromptText.textContent = '';
        return;
      }

      this.mobilePrompt.hidden = false;
      this.mobilePromptText.textContent = hasLeftEye
        ? 'Tap the center of the right pupil.'
        : 'Tap the center of the left pupil.';
    }

    getContainRect(contentWidth, contentHeight, boundsWidth, boundsHeight) {
      const contentRatio = contentWidth / contentHeight;
      const boundsRatio = boundsWidth / boundsHeight;

      if (contentRatio > boundsRatio) {
        const width = boundsWidth;
        const height = width / contentRatio;
        return { x: 0, y: (boundsHeight - height) / 2, width, height };
      }

      const height = boundsHeight;
      const width = height * contentRatio;
      return { x: (boundsWidth - width) / 2, y: 0, width, height };
    }

    getNormalizedPoint(event) {
      if (!(this.workspace instanceof HTMLElement) || !this.state.faceRect) return null;
      const rect = this.workspace.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      const faceRect = this.state.faceRect;

      if (x < faceRect.x || x > faceRect.x + faceRect.width || y < faceRect.y || y > faceRect.y + faceRect.height) {
        return null;
      }

      return {
        x: (x - faceRect.x) / faceRect.width,
        y: (y - faceRect.y) / faceRect.height,
      };
    }

    getRenderedPoint(point) {
      if (!point || !this.state.faceRect) return null;
      return {
        x: this.state.faceRect.x + point.x * this.state.faceRect.width,
        y: this.state.faceRect.y + point.y * this.state.faceRect.height,
      };
    }

    getMarkerAtPointerEvent(event) {
      if (!(this.workspace instanceof HTMLElement)) return null;

      const rect = this.workspace.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      const radius = (this.workspace.clientWidth / 16) / 2;
      const leftPoint = this.getRenderedPoint(this.state.leftEye);
      const rightPoint = this.getRenderedPoint(this.state.rightEye);

      if (leftPoint && Math.hypot(leftPoint.x - x, leftPoint.y - y) <= radius) {
        return 'left';
      }

      if (rightPoint && Math.hypot(rightPoint.x - x, rightPoint.y - y) <= radius) {
        return 'right';
      }

      return null;
    }

    positionMarker(element, point) {
      if (!(element instanceof HTMLElement)) return;
      if (!point) {
        element.hidden = true;
        return;
      }

      element.hidden = false;
      element.style.left = `${point.x}px`;
      element.style.top = `${point.y}px`;
    }

    hideMarkers() {
      this.positionMarker(this.leftMarker, null);
      this.positionMarker(this.rightMarker, null);
    }

    shouldUseLoupe() {
      return window.innerWidth <= 749;
    }

    updateLoupe(event) {
      if (!(this.loupe instanceof HTMLElement) || !(this.loupeCanvas instanceof HTMLCanvasElement)) return;
      if (!(this.workspace instanceof HTMLElement) || !(this.canvas instanceof HTMLCanvasElement)) return;

      const rect = this.workspace.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      if (x < 0 || y < 0 || x > rect.width || y > rect.height) {
        this.hideLoupe();
        return;
      }

      const loupeSize = 112;
      const zoom = 2;
      const sampleSize = loupeSize / zoom;
      const dpr = window.devicePixelRatio || 1;
      const context = this.loupeCanvas.getContext('2d');
      if (!context) return;

      this.loupe.hidden = false;
      this.loupeCanvas.width = loupeSize;
      this.loupeCanvas.height = loupeSize;
      context.clearRect(0, 0, loupeSize, loupeSize);
      context.imageSmoothingEnabled = false;
      context.drawImage(
        this.canvas,
        Math.max(0, (x - sampleSize / 2) * dpr),
        Math.max(0, (y - sampleSize / 2) * dpr),
        sampleSize * dpr,
        sampleSize * dpr,
        0,
        0,
        loupeSize,
        loupeSize
      );

      context.strokeStyle = 'rgba(214, 46, 46, 0.95)';
      context.lineWidth = 2;
      context.beginPath();
      context.moveTo(loupeSize / 2, 18);
      context.lineTo(loupeSize / 2, loupeSize - 18);
      context.moveTo(18, loupeSize / 2);
      context.lineTo(loupeSize - 18, loupeSize / 2);
      context.stroke();
    }

    hideLoupe() {
      if (!(this.loupe instanceof HTMLElement)) return;
      this.loupe.hidden = true;
    }

    toggleCameraPanel(isVisible) {
      if (!(this.cameraPanel instanceof HTMLElement)) return;
      this.root.classList.toggle('try-on-experience--camera-open', isVisible);
      if (isVisible) {
        this.cameraPanel.removeAttribute('hidden');
      } else {
        this.cameraPanel.setAttribute('hidden', '');
      }
    }

    stopCamera() {
      this.stream?.getTracks().forEach((track) => track.stop());
      this.stream = null;
      if (this.video instanceof HTMLVideoElement) this.video.srcObject = null;
    }

    showError(message) {
      if (!(this.error instanceof HTMLElement)) return;
      this.error.textContent = message;
      this.error.hidden = false;
    }

    clearError() {
      if (!(this.error instanceof HTMLElement)) return;
      this.error.textContent = '';
      this.error.hidden = true;
    }

    setPdFieldError(message) {
      this.pdInputs.forEach((input) => {
        input.setAttribute('aria-invalid', 'true');
        input.classList.add('is-invalid');
      });

      this.pdErrors.forEach((error) => {
        error.textContent = message;
        error.hidden = false;
      });
    }

    clearPdFieldError() {
      this.pdInputs.forEach((input) => {
        input.removeAttribute('aria-invalid');
        input.classList.remove('is-invalid');
      });

      this.pdErrors.forEach((error) => {
        error.textContent = '';
        error.hidden = true;
      });
    }

    syncPdInputs(value, sourceInput = null) {
      this.pdInputs.forEach((input) => {
        if (sourceInput && input === sourceInput) return;
        input.value = value;
      });
    }

    updateStatus(message, promptMessage = message) {
      if (!(this.status instanceof HTMLElement)) return;

      if (this.statusText instanceof HTMLElement) {
        this.statusText.textContent = message;
      } else {
        this.status.textContent = message;
      }

      if (this.referencesCopy instanceof HTMLElement) {
        this.referencesCopy.textContent = promptMessage;
      }

      this.status.classList.remove('is-highlighted');
      window.clearTimeout(this.statusHighlightTimeout);
      window.requestAnimationFrame(() => {
        this.status.classList.add('is-highlighted');
      });
      this.statusHighlightTimeout = window.setTimeout(() => {
        this.status?.classList.remove('is-highlighted');
      }, 850);
    }

    loadImage(src) {
      return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = src;
      });
    }

    readFileAsDataUrl(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    }

    getPdRangeLabel() {
      const { pdMin, pdMax } = this.getPdBounds();
      return `${pdMin} mm and ${pdMax} mm`;
    }

    getPdBounds() {
      const pdMin = Number(this.state.data.pdMinMm);
      const pdMax = Number(this.state.data.pdMaxMm);

      return {
        pdMin: Number.isFinite(pdMin) && pdMin > 0 ? pdMin : 40,
        pdMax: Number.isFinite(pdMax) && pdMax > 0 ? pdMax : 80,
      };
    }

    logDebugSnapshot(stage) {
      console.groupCollapsed(`[TryOn] ${stage}`);
      console.log('productId', this.state.data.productId);
      console.log('productTitle', this.state.data.productTitle);
      console.log('frameWidthMm', this.state.data.frameWidthMm);
      console.log('pdMinMm', this.state.data.pdMinMm);
      console.log('pdMaxMm', this.state.data.pdMaxMm);
      console.log('defaultImageUrl', this.state.data.defaultImageUrl);
      console.log('imagesByColor', this.state.data.imagesByColor || {});
      console.log('selectedColor', this.state.selectedColor);
      console.log('activeImageUrl', this.state.activeImageUrl);
      console.log('liquidAvailability', this.state.data.isAvailable);
      console.log('liquidDebug', this.state.data.debug || {});
      console.log('allImageAlts', this.state.data.debug?.allImageAlts || []);
      console.groupEnd();
    }
  }

  function initializeTryOn(root) {
    if (!root || root.dataset.tryOnInitialized === 'true') return;
    root.dataset.tryOnInitialized = 'true';
    new TryOnExperience(root);
  }

  function initializeAllTryOn() {
    document.querySelectorAll('[data-try-on-root]').forEach((root) => initializeTryOn(root));
  }

  document.addEventListener('DOMContentLoaded', initializeAllTryOn);
  document.addEventListener('shopify:section:load', initializeAllTryOn);
})();
